import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from 'recharts';
import { Slider } from '../components/ui/slider';
import { Calculator, TrendingUp, PiggyBank, Plus, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '../layouts/AppLayout';
import { Footer } from '../components/legal';
import { Navigation } from '../components/Navigation';

// German number formatting
const formatCurrency = (value) => {
  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatPercent = (value) => {
  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value);
};

// Scenario colors
const SCENARIO_COLORS = {
  1: '#10b981', // emerald
  2: '#3b82f6', // blue
  3: '#f59e0b', // amber
};

// Compound interest calculation
const calculateCompoundInterest = ({
  initialCapital,
  monthlyContribution,
  annualInterestRate,
  years,
  isMonthly,
  dynamicRate,
}) => {
  const periodsPerYear = isMonthly ? 12 : 1;
  const totalPeriods = years * periodsPerYear;
  const periodRate = annualInterestRate / 100 / periodsPerYear;
  
  let balance = initialCapital;
  let totalContributions = initialCapital;
  let currentContribution = isMonthly ? monthlyContribution : monthlyContribution * 12;
  
  const yearlyData = [];
  
  for (let period = 1; period <= totalPeriods; period++) {
    const interest = balance * periodRate;
    balance += interest;
    balance += currentContribution;
    totalContributions += currentContribution;
    
    const currentYear = Math.ceil(period / periodsPerYear);
    const isYearEnd = period % periodsPerYear === 0 || period === totalPeriods;
    
    if (isYearEnd) {
      if (dynamicRate > 0 && currentYear < years) {
        currentContribution *= (1 + dynamicRate / 100);
      }
      
      yearlyData.push({
        year: currentYear,
        totalCapital: Math.round(balance),
        contributions: Math.round(totalContributions),
      });
    }
  }
  
  const totalInterest = balance - totalContributions;
  
  return {
    finalCapital: Math.round(balance),
    totalContributions: Math.round(totalContributions),
    totalInterest: Math.round(totalInterest),
    yearlyData,
  };
};

// Default scenario values
const getDefaultScenario = () => ({
  initialCapital: 0,
  monthlyContribution: 200,
  savingsInterval: 'monatlich',
  dynamicRate: 0,
  interestRate: 6,
  years: 10,
});

// Custom Tooltip
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1a2235]/95 backdrop-blur-sm rounded-xl shadow-xl border border-white/10 p-3">
      <p className="text-xs font-medium text-white/60 mb-2">Jahr {label}</p>
      {payload.map((entry, index) => (
        <div key={index} className="flex items-center gap-2 text-xs">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-white/70">{entry.name}:</span>
          <span className="font-semibold text-white">{formatCurrency(entry.value)} €</span>
        </div>
      ))}
    </div>
  );
};

const ZinsenBerechnen = () => {
  const navigate = useNavigate();
  
  // Scenarios state
  const [scenarios, setScenarios] = useState([getDefaultScenario()]);
  const [activeScenario, setActiveScenario] = useState(0);
  
  // Get current scenario
  const currentScenario = scenarios[activeScenario];
  
  // Update a field in the current scenario
  const updateScenario = (field, value) => {
    setScenarios(prev => {
      const updated = [...prev];
      updated[activeScenario] = { ...updated[activeScenario], [field]: value };
      return updated;
    });
  };
  
  // Add a new scenario (copy from scenario 1)
  const addScenario = () => {
    if (scenarios.length < 3) {
      setScenarios(prev => [...prev, { ...prev[0] }]);
      setActiveScenario(scenarios.length);
    }
  };
  
  // Remove a scenario
  const removeScenario = (index) => {
    if (scenarios.length > 1) {
      setScenarios(prev => prev.filter((_, i) => i !== index));
      if (activeScenario >= index && activeScenario > 0) {
        setActiveScenario(activeScenario - 1);
      }
    }
  };
  
  // Calculate results for all scenarios
  const allResults = useMemo(() => {
    return scenarios.map(scenario => calculateCompoundInterest({
      initialCapital: scenario.initialCapital,
      monthlyContribution: scenario.monthlyContribution,
      annualInterestRate: scenario.interestRate,
      years: scenario.years,
      isMonthly: scenario.savingsInterval === 'monatlich',
      dynamicRate: scenario.dynamicRate,
    }));
  }, [scenarios]);
  
  // Prepare chart data with all scenarios
  const chartData = useMemo(() => {
    const maxYears = Math.max(...scenarios.map(s => s.years));
    const data = [];
    
    for (let year = 0; year <= maxYears; year++) {
      const point = { year };
      scenarios.forEach((scenario, index) => {
        const result = allResults[index];
        const yearData = result.yearlyData.find(d => d.year === year);
        if (year === 0) {
          point[`scenario${index + 1}`] = scenario.initialCapital;
          if (scenarios.length === 1) {
            point.contributions = scenario.initialCapital;
          }
        } else if (yearData) {
          point[`scenario${index + 1}`] = yearData.totalCapital;
          if (scenarios.length === 1) {
            point.contributions = yearData.contributions;
          }
        }
      });
      data.push(point);
    }
    
    return data;
  }, [scenarios, allResults]);

  const hasMultipleScenarios = scenarios.length > 1;

  return (
    <AppLayout>
      <Navigation variant="dark" />

      <div className="min-h-screen bg-gradient-to-b from-[#12172a] via-[#151b2d] to-[#12172a] pt-20 sm:pt-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          
          {/* Header */}
          <div className="text-center mb-8 sm:mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald/10 rounded-full text-emerald text-xs font-medium uppercase tracking-wider mb-3 sm:mb-4">
              <Calculator className="w-3 h-3" />
              Zinseszinsrechner
            </div>
            <h1 className="font-playfair text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3 sm:mb-4">
              Zinsen berechnen
            </h1>
            <p className="text-sm sm:text-base text-white/60 max-w-2xl mx-auto px-4">
              Berechnen Sie, wie sich Ihr Vermögen mit Zinseszins entwickelt.
            </p>
          </div>
          
          {/* Main Grid */}
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-8">
            
            {/* Input Section */}
            <motion.div 
              className="bg-[#1a2235]/80 backdrop-blur-sm rounded-2xl border border-white/5 p-4 sm:p-6 md:p-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {/* Header with Scenario Tabs */}
              <div className="flex items-center justify-between mb-6 sm:mb-8 gap-2">
                <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                  {/* Scenario Tabs */}
                  {scenarios.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveScenario(index)}
                      className={`relative flex items-center gap-1 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium transition-all ${
                        activeScenario === index
                          ? 'bg-white/10 text-white'
                          : 'text-white/50 hover:text-white/70 hover:bg-white/5'
                      }`}
                      style={{
                        borderLeft: activeScenario === index ? `3px solid ${SCENARIO_COLORS[index + 1]}` : 'none'
                      }}
                    >
                      <span className="hidden sm:inline">{hasMultipleScenarios ? `Szenario ${index + 1}` : 'Ihre Eingaben'}</span>
                      <span className="sm:hidden">{hasMultipleScenarios ? `S${index + 1}` : 'Eingaben'}</span>
                      
                      {/* Delete button for scenarios 2 and 3 */}
                      {index > 0 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeScenario(index);
                          }}
                          className="ml-0.5 p-0.5 rounded-full hover:bg-red-500/20 text-red-400 hover:text-red-300 transition-colors"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </button>
                  ))}
                </div>
                
                {/* Add Scenario Button */}
                {scenarios.length < 3 && (
                  <button
                    onClick={addScenario}
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-emerald/10 hover:bg-emerald/20 border-2 border-emerald/50 hover:border-emerald flex items-center justify-center text-emerald transition-all flex-shrink-0"
                    title="Szenario hinzufügen"
                  >
                    <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                )}
              </div>
              
              {/* Input Fields */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeScenario}
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  {/* Initial Capital */}
                  <div className="mb-6 sm:mb-8">
                    <div className="flex justify-between items-baseline mb-3 sm:mb-4">
                      <label className="text-xs sm:text-sm font-medium text-white/80">Anfangskapital</label>
                      <span className="font-mono text-lg sm:text-xl font-bold text-white">{formatCurrency(currentScenario.initialCapital)} €</span>
                    </div>
                    <div className="relative">
                      <div className="absolute inset-0 h-2.5 top-1/2 -translate-y-1/2 bg-white/10 rounded-full" />
                      <Slider
                        value={[currentScenario.initialCapital]}
                        onValueChange={(val) => updateScenario('initialCapital', val[0])}
                        min={0}
                        max={100000}
                        step={1000}
                        className="relative w-full [&_[role=slider]]:h-5 [&_[role=slider]]:w-5 [&_[role=slider]]:border-2 [&_[role=slider]]:border-emerald [&_[role=slider]]:bg-white [&_[role=slider]]:shadow-lg [&>span:first-child]:h-2.5 [&>span:first-child]:bg-transparent [&>span:first-child>span]:bg-emerald"
                      />
                    </div>
                    <div className="flex justify-between text-[10px] sm:text-xs font-medium text-white/50 mt-2 sm:mt-3">
                      <span className="bg-white/5 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">0 €</span>
                      <span className="bg-white/5 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">100.000 €</span>
                    </div>
                  </div>
                  
                  {/* Monthly Contribution */}
                  <div className="mb-6 sm:mb-8">
                    <div className="flex justify-between items-baseline mb-3 sm:mb-4">
                      <label className="text-xs sm:text-sm font-medium text-white/80">Sparrate</label>
                      <span className="font-mono text-lg sm:text-xl font-bold text-white">{formatCurrency(currentScenario.monthlyContribution)} €</span>
                    </div>
                    <div className="relative">
                      <div className="absolute inset-0 h-2.5 top-1/2 -translate-y-1/2 bg-white/10 rounded-full" />
                      <Slider
                        value={[currentScenario.monthlyContribution]}
                        onValueChange={(val) => updateScenario('monthlyContribution', val[0])}
                        min={0}
                        max={2000}
                        step={25}
                        className="relative w-full [&_[role=slider]]:h-5 [&_[role=slider]]:w-5 [&_[role=slider]]:border-2 [&_[role=slider]]:border-emerald [&_[role=slider]]:bg-white [&_[role=slider]]:shadow-lg [&>span:first-child]:h-2.5 [&>span:first-child]:bg-transparent [&>span:first-child>span]:bg-emerald"
                      />
                    </div>
                    <div className="flex justify-between text-[10px] sm:text-xs font-medium text-white/50 mt-2 sm:mt-3">
                      <span className="bg-white/5 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">0 €</span>
                      <span className="bg-white/5 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">2.000 €</span>
                    </div>
                  </div>
                  
                  {/* Interval & Dynamic */}
                  <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
                    <div>
                      <label className="text-xs sm:text-sm font-medium text-white/70 block mb-1.5 sm:mb-2">Sparintervall</label>
                      <select
                        value={currentScenario.savingsInterval}
                        onChange={(e) => updateScenario('savingsInterval', e.target.value)}
                        className="w-full bg-[#0f1420] border border-white/10 rounded-lg sm:rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base text-white focus:border-emerald/50 focus:outline-none transition-colors"
                      >
                        <option value="monatlich">monatlich</option>
                        <option value="jährlich">jährlich</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs sm:text-sm font-medium text-white/70 block mb-1.5 sm:mb-2">Dynamik</label>
                      <select
                        value={currentScenario.dynamicRate}
                        onChange={(e) => updateScenario('dynamicRate', Number(e.target.value))}
                        className="w-full bg-[#0f1420] border border-white/10 rounded-lg sm:rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base text-white focus:border-emerald/50 focus:outline-none transition-colors"
                      >
                        <option value={0}>keine</option>
                        <option value={1}>1% p.a.</option>
                        <option value={2}>2% p.a.</option>
                        <option value={3}>3% p.a.</option>
                        <option value={5}>5% p.a.</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Interest Rate */}
                  <div className="mb-6 sm:mb-8">
                    <div className="flex justify-between items-baseline mb-3 sm:mb-4">
                      <label className="text-xs sm:text-sm font-medium text-white/80">Zinssatz</label>
                      <span className="font-mono text-lg sm:text-xl font-bold text-emerald">{formatPercent(currentScenario.interestRate)} %</span>
                    </div>
                    <div className="relative">
                      <div className="absolute inset-0 h-2.5 top-1/2 -translate-y-1/2 bg-white/10 rounded-full" />
                      <Slider
                        value={[currentScenario.interestRate]}
                        onValueChange={(val) => updateScenario('interestRate', val[0])}
                        min={0}
                        max={15}
                        step={0.5}
                        className="relative w-full [&_[role=slider]]:h-5 [&_[role=slider]]:w-5 [&_[role=slider]]:border-2 [&_[role=slider]]:border-emerald [&_[role=slider]]:bg-white [&_[role=slider]]:shadow-lg [&>span:first-child]:h-2.5 [&>span:first-child]:bg-transparent [&>span:first-child>span]:bg-emerald"
                      />
                    </div>
                    <div className="flex justify-between text-[10px] sm:text-xs font-medium text-white/50 mt-2 sm:mt-3">
                      <span className="bg-white/5 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">0 %</span>
                      <span className="bg-white/5 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">15 %</span>
                    </div>
                  </div>
                  
                  {/* Years */}
                  <div className="mb-4 sm:mb-6">
                    <div className="flex justify-between items-baseline mb-3 sm:mb-4">
                      <label className="text-xs sm:text-sm font-medium text-white/80">Ansparzeit</label>
                      <span className="font-mono text-lg sm:text-xl font-bold text-white">{currentScenario.years} Jahre</span>
                    </div>
                    <div className="relative">
                      <div className="absolute inset-0 h-2.5 top-1/2 -translate-y-1/2 bg-white/10 rounded-full" />
                      <Slider
                        value={[currentScenario.years]}
                        onValueChange={(val) => updateScenario('years', val[0])}
                        min={1}
                        max={50}
                        step={1}
                        className="relative w-full [&_[role=slider]]:h-5 [&_[role=slider]]:w-5 [&_[role=slider]]:border-2 [&_[role=slider]]:border-emerald [&_[role=slider]]:bg-white [&_[role=slider]]:shadow-lg [&>span:first-child]:h-2.5 [&>span:first-child]:bg-transparent [&>span:first-child>span]:bg-emerald"
                      />
                    </div>
                    <div className="flex justify-between text-[10px] sm:text-xs font-medium text-white/50 mt-2 sm:mt-3">
                      <span className="bg-white/5 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">1 Jahr</span>
                      <span className="bg-white/5 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">50 Jahre</span>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </motion.div>
            
            {/* Results Section */}
            <motion.div 
              className="space-y-4 sm:space-y-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {/* Main Result Card */}
              <div className="bg-gradient-to-br from-emerald/20 via-teal-500/10 to-blue-500/10 rounded-2xl border border-emerald/20 p-5 sm:p-6 md:p-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-emerald/10 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-blue-500/10 rounded-full blur-2xl" />
                
                <div className="relative">
                  <p className="text-xs sm:text-sm font-medium text-emerald uppercase tracking-widest mb-3 sm:mb-4">
                    Endkapital
                  </p>
                  
                  {hasMultipleScenarios ? (
                    <div className="grid grid-cols-3 gap-2 sm:gap-4">
                      {scenarios.map((scenario, index) => (
                        <div key={index} className="text-center">
                          <div 
                            className="text-[10px] sm:text-xs font-medium mb-1 sm:mb-2 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full inline-block"
                            style={{ 
                              backgroundColor: `${SCENARIO_COLORS[index + 1]}20`,
                              color: SCENARIO_COLORS[index + 1]
                            }}
                          >
                            S{index + 1}
                          </div>
                          <p className="font-outfit text-lg sm:text-2xl md:text-3xl font-bold text-white tabular-nums">
                            {formatCurrency(allResults[index].finalCapital)} €
                          </p>
                          <p className="text-[10px] sm:text-xs text-white/40 mt-0.5 sm:mt-1">{scenario.years} J.</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="font-outfit text-4xl sm:text-5xl md:text-6xl font-bold text-white text-center tabular-nums">
                      {formatCurrency(allResults[0].finalCapital)} €
                    </p>
                  )}
                </div>
              </div>
              
              {/* Breakdown Cards */}
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                {/* Total Contributions */}
                <div className="bg-[#1a2235]/80 backdrop-blur-sm rounded-xl border border-white/5 p-4 sm:p-5">
                  <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-blue-500/10 rounded-lg flex items-center justify-center">
                      <PiggyBank className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-400" />
                    </div>
                    <span className="text-xs sm:text-sm text-white/60">Einzahlungen</span>
                  </div>
                  
                  {hasMultipleScenarios ? (
                    <div className="space-y-1.5 sm:space-y-2">
                      {scenarios.map((_, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <span 
                            className="text-[10px] sm:text-xs font-medium"
                            style={{ color: SCENARIO_COLORS[index + 1] }}
                          >
                            S{index + 1}
                          </span>
                          <span className="font-mono text-sm sm:text-lg font-bold text-white tabular-nums">
                            {formatCurrency(allResults[index].totalContributions)} €
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="font-mono text-xl sm:text-2xl font-bold text-white tabular-nums">
                      {formatCurrency(allResults[0].totalContributions)} €
                    </p>
                  )}
                </div>
                
                {/* Total Interest */}
                <div className="bg-[#1a2235]/80 backdrop-blur-sm rounded-xl border border-white/5 p-4 sm:p-5">
                  <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-emerald/10 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald" />
                    </div>
                    <span className="text-xs sm:text-sm text-white/60">Zinsen</span>
                  </div>
                  
                  {hasMultipleScenarios ? (
                    <div className="space-y-1.5 sm:space-y-2">
                      {scenarios.map((_, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <span 
                            className="text-[10px] sm:text-xs font-medium"
                            style={{ color: SCENARIO_COLORS[index + 1] }}
                          >
                            S{index + 1}
                          </span>
                          <span className="font-mono text-sm sm:text-lg font-bold text-emerald tabular-nums">
                            {formatCurrency(allResults[index].totalInterest)} €
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="font-mono text-xl sm:text-2xl font-bold text-emerald tabular-nums">
                      {formatCurrency(allResults[0].totalInterest)} €
                    </p>
                  )}
                </div>
              </div>
              
              {/* Interest Ratio */}
              <div className="bg-[#1a2235]/80 backdrop-blur-sm rounded-xl border border-white/5 p-4 sm:p-5">
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <span className="text-xs sm:text-sm text-white/60">Zinsen-Anteil</span>
                  {!hasMultipleScenarios && (
                    <span className="font-mono text-base sm:text-lg font-bold text-emerald">
                      {Math.round((allResults[0].totalInterest / allResults[0].finalCapital) * 100)}%
                    </span>
                  )}
                </div>
                
                {hasMultipleScenarios ? (
                  <div className="space-y-2 sm:space-y-3">
                    {scenarios.map((_, index) => {
                      const ratio = Math.round((allResults[index].totalInterest / allResults[index].finalCapital) * 100);
                      return (
                        <div key={index}>
                          <div className="flex justify-between text-[10px] sm:text-xs mb-1">
                            <span style={{ color: SCENARIO_COLORS[index + 1] }}>Szenario {index + 1}</span>
                            <span className="text-emerald font-medium">{ratio}%</span>
                          </div>
                          <div className="h-1.5 sm:h-2 bg-white/5 rounded-full overflow-hidden">
                            <div 
                              className="h-full rounded-full transition-all duration-500"
                              style={{ 
                                width: `${ratio}%`,
                                backgroundColor: SCENARIO_COLORS[index + 1]
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <>
                    <div className="h-2.5 sm:h-3 bg-white/5 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-blue-500 to-emerald rounded-full transition-all duration-500"
                        style={{ width: `${Math.round((allResults[0].totalInterest / allResults[0].finalCapital) * 100)}%` }}
                      />
                    </div>
                    <div className="flex justify-between mt-1.5 sm:mt-2 text-[10px] sm:text-xs text-white/40">
                      <span>Einzahlungen</span>
                      <span>Zinsen</span>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </div>
          
          {/* Chart Section */}
          <motion.div 
            className="mt-6 sm:mt-10 bg-[#1a2235]/80 backdrop-blur-sm rounded-2xl border border-white/5 p-4 sm:p-6 md:p-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
              <div>
                <h3 className="font-outfit text-lg sm:text-xl font-bold text-white mb-0.5 sm:mb-1">Vermögensentwicklung</h3>
                <p className="text-xs sm:text-sm text-white/50">Kapitalwachstum über die Zeit</p>
              </div>
              
              {/* Legend */}
              <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                {!hasMultipleScenarios && (
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-white/40" />
                    <span className="text-xs sm:text-sm text-white/60">Einzahlungen</span>
                  </div>
                )}
                {scenarios.map((_, index) => (
                  <div key={index} className="flex items-center gap-1.5 sm:gap-2">
                    <div 
                      className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full"
                      style={{ backgroundColor: SCENARIO_COLORS[index + 1] }}
                    />
                    <span className="text-xs sm:text-sm text-white/60">
                      {hasMultipleScenarios ? `S${index + 1}` : 'Vermögen'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="h-[280px] sm:h-[350px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis 
                    dataKey="year" 
                    stroke="rgba(255,255,255,0.4)"
                    tickLine={false}
                    axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                    tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }}
                    tickFormatter={(value) => `${value}J`}
                  />
                  <YAxis 
                    stroke="rgba(255,255,255,0.4)"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }}
                    tickFormatter={(value) => `€${(value / 1000).toFixed(0)}k`}
                    width={50}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  
                  {/* Contributions line - only in standard mode */}
                  {!hasMultipleScenarios && (
                    <Line
                      type="monotone"
                      dataKey="contributions"
                      name="Einzahlungen"
                      stroke="rgba(255,255,255,0.4)"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={false}
                      activeDot={{ r: 4, strokeWidth: 2, fill: 'rgba(255,255,255,0.6)' }}
                    />
                  )}
                  
                  {scenarios.map((_, index) => (
                    <Line
                      key={index}
                      type="monotone"
                      dataKey={`scenario${index + 1}`}
                      name={hasMultipleScenarios ? `Szenario ${index + 1}` : 'Vermögen'}
                      stroke={SCENARIO_COLORS[index + 1]}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 5, strokeWidth: 2, fill: SCENARIO_COLORS[index + 1] }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
          
          {/* CTA */}
          <motion.div 
            className="mt-8 sm:mt-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <p className="text-sm sm:text-base text-white/50 mb-4 sm:mb-6 px-4">
              Möchten Sie wissen, wie Sie Ihre Rendite mit einer optimierten Strategie steigern können?
            </p>
            <button
              onClick={() => navigate('/strategiegespraech')}
              className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-emerald to-teal-400 text-white rounded-full font-semibold text-sm sm:text-lg hover:shadow-lg hover:shadow-emerald/30 transition-all inline-flex items-center gap-2"
            >
              Strategiegespräch vereinbaren
              <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </motion.div>
        </div>
      </div>
      
      <Footer variant="dark" />
    </AppLayout>
  );
};

export default ZinsenBerechnen;
